/**
 * Hierarchy Dialog Component
 *
 * Purpose: Dialog for selecting hierarchical values with tree navigation
 * Architecture: Modal dialog with search, TreeView, and selection management
 * Author: Development Team
 * Created: 2025-01-21
 *
 * Features:
 * - Search functionality with Enter key trigger
 * - Lazy-loaded tree navigation with RichTreeView
 * - Single-select mode: only items at field's level are selectable
 * - Multiselect mode: all levels selectable with ancestor optimization
 * - Search match highlighting
 * - Selected count display with optimization
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Checkbox,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import type { RichItem, TreeNode } from '../../types/hierarchy';
import type { MasterDataValue } from '../../types/mdm';
import {
  buildTreeItems,
  optimizeSelection,
  isNodeSelectable,
  keepPickableSelections,
  shouldHideCheckbox,
  filterValidSelections,
  getSelectedValuesDisplayText,
  getNodePath,
} from '../../utils/hierarchyHelpers';
import { SecondaryButton } from '../common/ActionButtons';
import { SELECT_ALL_LABELS, type SelectAllMode } from '../../utils/selectAll';

/** Every node id in the loaded tree (what "Select All" shows ticked) */
const allLoadedIds = (nodes: TreeNode[]): string[] =>
  nodes.flatMap((node) => [node.id, ...allLoadedIds(node.children || [])]);

/** Every group in the tree as built: an item with children, loaded or still a placeholder */
export const expandableIds = (items: RichItem[]): string[] =>
  items.flatMap((item) =>
    item.children && item.children.length > 0 ? [item.id, ...expandableIds(item.children)] : []
  );

/** One level of indent in the tree, and the offset of the tree under the "Select All" row */
const INDENT_PX = 24;

/**
 * The highest pickable node on every loaded branch (ORCH-418 "All Available")
 *
 * Logic: normally the roots. In a scoped tree (ORCH-332) a root may be an ancestor that can
 *        be opened but not picked; then its loaded pickable descendants stand in for it.
 */
export const topPickableIds = (nodes: TreeNode[]): string[] =>
  nodes.flatMap((node) =>
    isNodeSelectable(node) ? [node.id] : topPickableIds(node.children || [])
  );
/**
 * Props interface for HierarchyDialog
 */
export interface HierarchyDialogProps {
  /**
   * Whether dialog is open
   */
  open: boolean;

  /**
   * Field label
   */
  label: string;

  /**
   * Whether multiselect is enabled
   */
  isMultiselect: boolean;

  /**
   * Field's hierarchy level (1-based)
   */
  fieldLevel: number;

  /**
   * Maximum hierarchy depth
   */
  hierarchyDepth: number;

  /**
   * Root tree nodes
   */
  rootNodes: TreeNode[];

  /**
   * Expanded node IDs
   */
  expandedNodes: string[];

  /**
   * Selected node IDs
   */
  selectedNodeIds: string[];

  /**
   * Search term
   */
  searchTerm: string;

  /**
   * Search match node IDs
   */
  searchMatchIds: string[];

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error message
   */
  error: string | null;

  /**
   * Node cache
   */
  nodeCache: Map<string, TreeNode>;

  /**
   * Close handler
   */
  onClose: () => void;

  /**
   * Apply handler
   */
  onApply: () => void;

  /**
   * Search term change handler
   */
  onSearchTermChange: (term: string) => void;

  /**
   * Search trigger handler
   */
  onSearch: (term: string) => void;

  /**
   * Expanded nodes change handler
   */
  onExpandedNodesChange: (nodeIds: string[]) => void;

  /**
   * Selected nodes change handler
   */
  onSelectedNodesChange: (nodeIds: string[]) => void;

  /**
   * Node expand handler
   */
  onNodeExpand: (nodeId: string) => Promise<void>;

  /**
   * Test ID for testing
   */
  dataTestId?: string;

  /** ORCH-418: which "All" row to show above the tree ('off' hides it) */
  selectAllMode?: SelectAllMode;

  /** ORCH-418: "All" (store ALL) is ticked */
  allSelected?: boolean;

  /** ORCH-418: the "All" row in 'all' mode was ticked or unticked */
  onSelectAllChange?: (checked: boolean) => void;

  /** ORCH-400: selected ids outside this user's access; always kept, checkbox disabled */
  locked?: ReadonlySet<string>;

  /** ORCH-418: load and expand every group ("Expand All"); the row's button is hidden without it */
  onExpandAll?: () => Promise<void>;
}

/**
 * Hierarchy Dialog Component
 *
 * Purpose: Provide tree-based interface for hierarchical value selection
 * Logic:
 * - Displays tree with lazy loading
 * - Supports search with expansion to matches
 * - Single-select: only field's level items selectable
 * - Multiselect: all levels selectable
 * - Shows optimized selection count
 *
 * Input: HierarchyDialogProps
 * Output: JSX.Element
 *
 * Example:
 * ```tsx
 * <HierarchyDialog
 *   open={open}
 *   label="Country"
 *   rootNodes={rootNodes}
 *   onClose={handleClose}
 *   onApply={handleApply}
 * />
 * ```
 */
const NO_LOCKED: ReadonlySet<string> = new Set();

export const HierarchyDialog: React.FC<HierarchyDialogProps> = ({
  open,
  label,
  isMultiselect,
  fieldLevel,
  hierarchyDepth,
  rootNodes,
  expandedNodes,
  selectedNodeIds,
  searchTerm,
  searchMatchIds,
  loading,
  error,
  nodeCache,
  onClose,
  onApply,
  onSearchTermChange,
  onSearch,
  onExpandedNodesChange,
  onSelectedNodesChange,
  onNodeExpand,
  dataTestId = 'hierarchy-dialog',
  selectAllMode = 'off',
  allSelected = false,
  onSelectAllChange,
  locked = NO_LOCKED,
  onExpandAll,
}) => {
  /**
   * Build tree items for RichTreeView
   */
  const treeItems = useMemo(() => {
    return buildTreeItems(rootNodes, hierarchyDepth);
  }, [rootNodes, hierarchyDepth]);

  // ORCH-418: "Expand All" / "Collapse All" on the "Select All" row. Shown only when the list
  // has a group to open; reads "Collapse All" once every group in the built tree is open.
  // While a search is active, Collapse All keeps the matching branches open, as the search
  // left them.
  const groupIds = useMemo(() => expandableIds(treeItems), [treeItems]);
  const allExpanded = groupIds.length > 0 && groupIds.every((id) => expandedNodes.includes(id));
  const searchExpansion = useMemo(
    () => Array.from(new Set(searchMatchIds.flatMap((id) => getNodePath(id, rootNodes)))),
    [searchMatchIds, rootNodes]
  );
  const [expanding, setExpanding] = useState(false);
  const handleExpandToggle = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      if (allExpanded) {
        onExpandedNodesChange(searchExpansion);
        return;
      }
      if (!onExpandAll) return;
      setExpanding(true);
      try {
        await onExpandAll();
      } catch (err) {
        console.error('[HierarchyDialog] Error expanding every group:', err);
      } finally {
        setExpanding(false);
      }
    },
    [allExpanded, onExpandAll, onExpandedNodesChange, searchExpansion]
  );

  /**
   * Handle search field key down
   */
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSearch(searchTerm);
      }
    },
    [searchTerm, onSearch]
  );

  /**
   * Handle clear search
   */
  const handleClearSearch = useCallback(() => {
    onSearchTermChange('');
    onSearch('');
  }, [onSearchTermChange, onSearch]);

  /**
   * Handle expanded items change
   */
  const handleExpandedItemsChange = useCallback(
    (_event: any, nodeIds: string[]) => {
      const newExpanded = Array.isArray(nodeIds) ? nodeIds : [nodeIds];
      const prevExpanded = expandedNodes;
      onExpandedNodesChange(newExpanded);

      // Load children for newly expanded nodes
      newExpanded.forEach((nodeId: string) => {
        if (!prevExpanded.includes(nodeId)) {
          onNodeExpand(nodeId).catch((err) => {
            console.error('[HierarchyDialog] Error expanding node:', err);
          });
        }
      });
    },
    [expandedNodes, onExpandedNodesChange, onNodeExpand]
  );

  /**
   * Handle selected items change
   */
  // ORCH-418: while "All" holds, every loaded node shows ticked, including children
  // loaded after it was ticked
  const shownSelection = useMemo(
    () => (allSelected ? allLoadedIds(rootNodes) : selectedNodeIds),
    [allSelected, rootNodes, selectedNodeIds]
  );

  // What the explicit rows act on: the pickable search matches, or the top pickable nodes
  const selectAllTargets = useMemo(() => {
    if (selectAllMode === 'matching') {
      return searchMatchIds.filter((id) => {
        const node = nodeCache.get(id);
        return node !== undefined && isNodeSelectable(node);
      });
    }
    if (selectAllMode === 'available') return topPickableIds(rootNodes);
    return [];
  }, [selectAllMode, searchMatchIds, nodeCache, rootNodes]);

  const selectAllChecked =
    selectAllMode === 'all'
      ? allSelected
      : selectAllTargets.length > 0 && selectAllTargets.every((id) => selectedNodeIds.includes(id));
  const selectAllIndeterminate =
    !selectAllChecked &&
    (selectAllMode === 'all'
      ? selectedNodeIds.length > 0
      : selectAllTargets.some((id) => selectedNodeIds.includes(id)));
  const selectAllDisabled = selectAllMode !== 'all' && selectAllTargets.length === 0;

  const handleSelectAllClick = useCallback(() => {
    if (selectAllMode === 'all') {
      onSelectAllChange?.(!allSelected);
      if (allSelected) onSelectedNodesChange([]);
      return;
    }
    if (selectAllChecked) {
      onSelectedNodesChange(selectedNodeIds.filter((id) => !selectAllTargets.includes(id)));
    } else {
      onSelectedNodesChange([
        ...selectedNodeIds,
        ...selectAllTargets.filter((id) => !selectedNodeIds.includes(id)),
      ]);
    }
  }, [
    selectAllMode,
    allSelected,
    selectAllChecked,
    selectAllTargets,
    selectedNodeIds,
    onSelectAllChange,
    onSelectedNodesChange,
  ]);

  const handleSelectedItemsChange = useCallback(
    (_event: any, itemIds: string | string[] | null) => {
      const picked = itemIds === null ? [] : Array.isArray(itemIds) ? itemIds : [itemIds];
      // ORCH-418: unticking a node while "All" holds turns All off; the rest stays ticked
      if (allSelected) {
        onSelectAllChange?.(false);
        onSelectedNodesChange(keepPickableSelections(picked, nodeCache, shownSelection, locked));
        return;
      }
      // ORCH-332: ancestors outside the form's scope can't be newly picked
      // ORCH-400: a locked value can't be unticked
      const ids = keepPickableSelections(picked, nodeCache, selectedNodeIds, locked);

      // In single-select mode, filter selections to only allow items at the field's hierarchy level
      if (!isMultiselect) {
        // Filter out any selections that are not at the correct level
        const validIds = filterValidSelections(ids, nodeCache, fieldLevel);

        // Only allow one selection in single-select mode
        if (validIds.length > 1) {
          // Keep only the most recently selected item
          const newlySelected = validIds.find((id) => !selectedNodeIds.includes(id));
          onSelectedNodesChange(newlySelected ? [newlySelected] : validIds.slice(-1));
        } else {
          onSelectedNodesChange(validIds);
        }
      } else {
        // Multiselect mode - allow any selection
        onSelectedNodesChange(ids);
      }
    },
    [
      isMultiselect,
      nodeCache,
      fieldLevel,
      selectedNodeIds,
      onSelectedNodesChange,
      allSelected,
      onSelectAllChange,
      shownSelection,
      locked,
    ]
  );

  /**
   * Get optimized selected values for display
   */
  const optimizedSelectionDisplay = useMemo(() => {
    if (!isMultiselect || selectedNodeIds.length === 0) {
      return null;
    }

    // Apply same optimization logic as handleApply to show only top-level ancestors
    const selected = selectedNodeIds
      .map((id) => nodeCache.get(id)?.value)
      .filter((v): v is MasterDataValue => v !== undefined);

    const optimized = optimizeSelection(selected);
    const displayText = getSelectedValuesDisplayText(optimized);

    return {
      count: selectedNodeIds.length,
      displayText,
    };
  }, [isMultiselect, selectedNodeIds, nodeCache]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
      data-testid={dataTestId}
    >
      <DialogTitle data-testid={`${dataTestId}-title`}>
        {isMultiselect ? `Select ${label}` : `Select ${label}`}
      </DialogTitle>
      <DialogContent>
        {/* Search input */}
        <TextField
          label="Search hierarchy"
          placeholder="Type and press Enter to search"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  data-testid={`${dataTestId}-search-clear`}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          margin="normal"
          data-testid={`${dataTestId}-search-input`}
        />

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid={`${dataTestId}-error`}>
            {error}
          </Alert>
        )}

        {/* TreeView */}
        <Box sx={{ mt: 2, maxHeight: '50vh', overflow: 'auto', minHeight: '200px' }}>
          {loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
              data-testid={`${dataTestId}-loading`}
            >
              <CircularProgress />
            </Box>
          ) : rootNodes.length === 0 ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
              data-testid={`${dataTestId}-empty`}
            >
              <Typography variant="body2" color="text.secondary">
                {error || 'No items available'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              {/* ORCH-418: the "Select All" row is the parent of the whole list: leftmost,
                  with every tree node indented one level beneath it. It is outside the tree so
                  selection propagation never treats it as a parent of the roots, and it carries
                  no chevron (a chevron on a root row would hide the list, which is not
                  "expand every group"). Its layout mirrors a TreeItem row: the same padding,
                  gap and label type, with an empty icon slot where a chevron would sit. */}
              {isMultiselect && selectAllMode !== 'off' && treeItems.length > 0 && (
                <Box
                  data-testid={`${dataTestId}-select-all`}
                  role="checkbox"
                  aria-checked={selectAllIndeterminate ? 'mixed' : selectAllChecked}
                  aria-label={SELECT_ALL_LABELS[selectAllMode]}
                  tabIndex={0}
                  onClick={handleSelectAllClick}
                  onKeyDown={(event) => {
                    if (event.key === ' ' || event.key === 'Enter') {
                      event.preventDefault();
                      handleSelectAllClick();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: selectAllDisabled ? 'default' : 'pointer',
                    opacity: selectAllDisabled ? 0.5 : 1,
                    pointerEvents: selectAllDisabled ? 'none' : 'auto',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  {/* The chevron slot, kept empty so the checkbox lines up with a root row's */}
                  <Box sx={{ width: 16, flexShrink: 0 }} />
                  <Checkbox
                    checked={selectAllChecked}
                    indeterminate={selectAllIndeterminate}
                    disabled={selectAllDisabled}
                    tabIndex={-1}
                    inputProps={{ 'aria-hidden': true }}
                    sx={{ p: 0 }}
                  />
                  <Typography variant="body1" sx={{ flexGrow: 1, minWidth: 0 }}>
                    {SELECT_ALL_LABELS[selectAllMode]}
                  </Typography>
                  {groupIds.length > 0 && onExpandAll && (
                    <Button
                      variant="text"
                      onClick={handleExpandToggle}
                      disabled={expanding}
                      sx={{ my: -0.5, py: 0 }}
                      data-testid={`${dataTestId}-expand-all`}
                    >
                      {allExpanded ? 'Collapse All' : 'Expand All'}
                    </Button>
                  )}
                </Box>
              )}
              {treeItems.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2 }}
                  data-testid={`${dataTestId}-no-nodes`}
                >
                  No valid nodes to display
                </Typography>
              ) : (
                <RichTreeView
                  id="hierarchy-tree-view"
                  items={treeItems}
                  expandedItems={expandedNodes}
                  selectedItems={shownSelection}
                  checkboxSelection
                  multiSelect={true}
                  selectionPropagation={{
                    descendants: isMultiselect,
                    parents: isMultiselect,
                  }}
                  isItemDisabled={(item) => {
                    // Disable placeholder nodes (loading indicators)
                    const itemId = typeof item === 'string' ? item : (item as any)?.id;
                    if (itemId && itemId.includes('-loading-placeholder')) {
                      return true;
                    }
                    return false;
                  }}
                  onExpandedItemsChange={handleExpandedItemsChange}
                  onSelectedItemsChange={handleSelectedItemsChange}
                  slotProps={{
                    item: (ownerState: any) => {
                      const isSearchMatch = searchMatchIds.includes(ownerState.itemId);

                      // Hide checkboxes for levels that can't be picked (single-select) and
                      // for ancestors outside the form's scope (ORCH-332), except one
                      // already selected, which stays visible so it can be unticked
                      let hideCheckbox = false;
                      const itemId = ownerState.itemId;
                      if (itemId && !itemId.includes('-loading-placeholder')) {
                        const node = nodeCache.get(itemId);
                        if (node) {
                          hideCheckbox =
                            shouldHideCheckbox(node, fieldLevel, isMultiselect) &&
                            !(selectedNodeIds.includes(itemId) && !isNodeSelectable(node));
                        }
                      }

                      const isLocked = !!itemId && locked.has(itemId);

                      return {
                        sx: {
                          ...(isLocked && {
                            '& > .MuiTreeItem-content > .MuiTreeItem-checkbox': {
                              pointerEvents: 'none',
                              opacity: 0.5,
                            },
                          }),
                          ...(isSearchMatch && {
                            '& .MuiTreeItem-label': {
                              fontWeight: 'bold',
                              color: 'primary.main',
                            },
                          }),
                          ...(hideCheckbox && {
                            '& > .MuiTreeItem-content > .MuiTreeItem-checkbox': {
                              display: 'none',
                            },
                          }),
                        },
                      };
                    },
                  }}
                  itemChildrenIndentation={INDENT_PX}
                  sx={{
                    width: '100%',
                    // One level under the "Select All" row
                    pl: `${INDENT_PX}px`,
                  }}
                  data-testid={`${dataTestId}-tree`}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Selected count (multiselect only) */}
        {optimizedSelectionDisplay && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2 }}
            data-testid={`${dataTestId}-selection-count`}
          >
            Selected ({optimizedSelectionDisplay.count}): {optimizedSelectionDisplay.displayText}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose} data-testid={`${dataTestId}-cancel-button`}>
          Cancel
        </SecondaryButton>
        <Button onClick={onApply} variant="contained" data-testid={`${dataTestId}-apply-button`}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HierarchyDialog;
